// src/hooks/useAdminJobs.js 
import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

const useAdminJobs = () => {
  const [pendingJobs, setPendingJobs] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewHistory, setReviewHistory] = useState([]);
  
  const { showSuccess, showError } = useToast();
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  // Load admin dashboard data
  const loadAdminData = useCallback(async (showLoader = true) => {
    if (!mountedRef.current) return;
    
    try {
      if (showLoader) setLoading(true);
      setError(null);

      const [pendingResponse, statsResponse] = await Promise.all([
        apiService.getPendingJobs(1, 50),
        apiService.getAdminDashboardStats(),
      ]);

      if (mountedRef.current) {
        setPendingJobs(pendingResponse.jobs || []);
        setAdminStats(statsResponse);
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (mountedRef.current) {
        setError(err.message || 'Failed to load admin data');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Load review history
  const loadReviewHistory = useCallback(async (page = 1, limit = 20, actionFilter = null) => {
    if (!mountedRef.current) return null;
    
    try {
      const response = await apiService.getReviewedJobsHistory(page, limit, actionFilter);
      if (mountedRef.current) {
        setReviewHistory(response.jobs || []);
      }
      return response;
    } catch (err) {
      console.error('Error loading review history:', err);
      showError('Failed to load review history');
      return null;
    }
  }, [showError]);

  const approveJob = useCallback(async (jobId, notes = '') => {
    if (!mountedRef.current) return null;
    
    try {
      console.log(`🔄 Admin approving job ${jobId}`);
      const response = await apiService.reviewJob(jobId, 'approve', notes);
      
      if (mountedRef.current) {
        // Remove from pending jobs immediately
        setPendingJobs(prev => {
          const filtered = prev.filter(job => job.id !== jobId);
          console.log(`📝 Removed job ${jobId} from pending list. Remaining: ${filtered.length}`);
          return filtered;
        });
        
        // Update stats
        if (adminStats) {
          setAdminStats(prev => ({
            ...prev,
            total_pending_approval: Math.max(0, prev.total_pending_approval - 1),
            total_approved_today: prev.total_approved_today + 1,
          }));
        }

        showSuccess(`Job approved successfully! Processing will begin shortly.`);
        
        //Trigger a refresh after a short delay to ensure backend is updated
        setTimeout(() => {
          if (mountedRef.current) {
            loadAdminData(false);
          }
        }, 2000);
      }
      
      return response;
    } catch (err) {
      console.error('Error approving job:', err);
      showError(err.message || 'Failed to approve job');
      throw err;
    }
  }, [adminStats, showSuccess, showError, loadAdminData]);

  const rejectJob = useCallback(async (jobId, notes = '') => {
    if (!notes.trim()) {
      showError('Please provide a reason for rejection');
      return null;
    }

    if (!mountedRef.current) return null;

    try {
      console.log(`🔄 Admin rejecting job ${jobId}`);
      const response = await apiService.reviewJob(jobId, 'reject', notes);
      
      if (mountedRef.current) {
        // Remove from pending jobs immediately
        setPendingJobs(prev => {
          const filtered = prev.filter(job => job.id !== jobId);
          console.log(`📝 Removed job ${jobId} from pending list. Remaining: ${filtered.length}`);
          return filtered;
        });
        
        // Update stats
        if (adminStats) {
          setAdminStats(prev => ({
            ...prev,
            total_pending_approval: Math.max(0, prev.total_pending_approval - 1),
            total_rejected_today: prev.total_rejected_today + 1,
          }));
        }

        showSuccess(`Job rejected successfully.`);
        
        setTimeout(() => {
          if (mountedRef.current) {
            loadAdminData(false);
          }
        }, 2000);
      }

      return response;
    } catch (err) {
      console.error('Error rejecting job:', err);
      showError(err.message || 'Failed to reject job');
      throw err;
    }
  }, [adminStats, showSuccess, showError, loadAdminData]);

  // Get job details
  const getJobDetails = useCallback(async (jobId) => {
    try {
      const response = await apiService.getPendingJobDetails(jobId);
      return response;
    } catch (err) {
      console.error('Error getting job details:', err);
      showError('Failed to load job details');
      return null;
    }
  }, [showError]);

  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      if (pendingJobs.length > 0) {
        console.log(`🔄 Starting admin polling for ${pendingJobs.length} pending jobs`);
        pollingIntervalRef.current = setInterval(() => {
          if (mountedRef.current) {
            console.log('📡 Admin polling - refreshing pending jobs...');
            loadAdminData(false);
          }
        }, 30000); // Check every 30 seconds
      } else {
        console.log('✅ No pending jobs, admin polling stopped');
      }
    };

    startPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pendingJobs.length, loadAdminData]);

  // Component unmount cleanup
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Initial load
  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const refreshAdminData = useCallback(async () => {
    await loadAdminData(false);
  }, [loadAdminData]);

  return {
    // Data
    pendingJobs,
    adminStats,
    reviewHistory,
    loading,
    error,

    // Actions
    loadAdminData,
    loadReviewHistory,
    approveJob,
    rejectJob,
    getJobDetails,
    refreshAdminData, 

    // Computed values
    hasPendingJobs: pendingJobs.length > 0,
    pendingCount: pendingJobs.length,
  };
};

export default useAdminJobs;